import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { UtilService } from 'app/shared/util.service';
import { trackByUuid } from 'app/utils';
import { User } from 'app/core/user/user.types';
import { PlatformService } from 'app/core/platform/platform.service';
import { AuthService } from 'app/core/auth/auth.service';
import { NewArtistModalComponent } from 'app/shared/components/new-artist-modal/new-artist-modal';
import { ReleasesService } from 'app/modules/user/releases/releases.service';
import { Track, Genre, Artist, ParticipantRole, AudioFormat, TrackParticipant, Language } from 'app/modules/user/releases/releases.types';

@Component({
    selector: 'app-track-details',
    templateUrl: './track-details.component.html',
    styleUrls: ['./track-details.component.css'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrackDetailsComponent implements OnInit, OnDestroy {
    @ViewChild('artistTrackInput') artistInput: ElementRef<HTMLInputElement>;
    @ViewChild('featArtistTrackInput') featArtistInput: ElementRef<HTMLInputElement>;

    trackIndex: number;
    track: Track;
    originalTrackData: Track;
    fillData: Track;
    cdn: string;
    genres: Genre[];
    user: User;
    artists: Artist[];
    artists$: Observable<Artist[]>;
    filteredArtists: Artist[];
    languages: Language[];
    trackForm: FormGroup;
    audioPlayer: HTMLAudioElement;
    participantRole = ParticipantRole;
    participantRoles = Object.values(ParticipantRole).sort((a, b) => a.localeCompare(b));
    genreFilterSubject = new Subject<Event>;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _audioStopThreshold: number;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        public matDialogRef: MatDialogRef<TrackDetailsComponent>,
        private _changeDetectorRef: ChangeDetectorRef,
        private _formBuilder: FormBuilder,
        private _releasesService: ReleasesService,
        public utilService: UtilService,
        public platformService: PlatformService,
        private _authService: AuthService,
        private _matDialog: MatDialog,
    ) {
    }

    ngOnInit(): void {
        this.cdn = this.data.cdn;
        this.genres = this.data.genres;
        this.artists$ = this.data.artists$;
        this.languages = this.data.languages;
        this.track = this.data.track;
        this.track.audioFile = this.track.audioFiles.find(f => f.type === AudioFormat.Master);
        this.user = this.data.user;
        this.trackIndex = this.data.trackIndex;
        this.fillData = this.data.fillData;
        this.filteredArtists = this.data.artists;
        this.originalTrackData = Object.assign(this.track);

        this.trackForm = this._formBuilder.group({
            title: ['', Validators.required],
            version: [''],
            participants: this._formBuilder.array([], [this.participantValidator.bind(this)(), this.maxShareValidator.bind(this)()]),
            lyrics: [''],
            primaryGenre: [null, Validators.required],
            subGenre: [null],
            isrc: ['', [this.isrcValidator.bind(this)()]],
            iswc: [''],
            language: ['0', Validators.required],
            explicit: [false],
            previewStartString: ['0:00', Validators.pattern(/^([0-9]{1,2}):[0-5][0-9]$/)],
            previewDuration: [30]
        });

        if (!this.track.participants) {
            this.track.participants = [];
        }

        if (this.track.participants?.length === 0) {
            this.addParticipant({ role: ParticipantRole.MainArtist, participant: null, share: null, email: null })
        } else {
            for (const participant of this.track.participants) {
                this.addParticipant(participant);
            }

            this.track.participants.sort((a, b) => a.sortOrder - b.sortOrder);
            for (let [i, participant] of this.track.participants.entries()) {
                this.track.participants[i].share = Math.round(participant.share * 10000) / 100;
            }
        }

        if (this.track.language === null) {
            this.track.language = '0';
        }

        // Fill the form
        this.trackForm.patchValue(this.track, { onlySelf: true, emitEvent: false });

        this.artists$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((artists: Artist[]) => {
                this.artists = artists;
                this.filterParticipants();
                this._changeDetectorRef.markForCheck();
            });

        // Update card when there is a value change on the card form
        /*this.trackForm.valueChanges
            .pipe(
                tap((value) => {

                    // Update the card object
                    this.track = assign(this.track, value);
                }),
                debounceTime(300),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((value) => {

                // Update the card on the server
                this._scrumboardService.updateCard(value.id, value).subscribe();

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });*/

        window['autofill'] = this.fillData; //TODO remove
        window['track'] = this.track; //TODO remove
        window['trackForm'] = this.trackForm; //TODO remove
        window['artists'] = this.artists; //TODO remove
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        if (this.audioPlayer) {
            this.audioPlayer.pause();
            this.audioPlayer.removeEventListener('timeupdate', this.previewPlayerTimeUpdate.bind(this));
            this.audioPlayer.remove();
            this.audioPlayer = undefined;
        }
    }

    participantValidator(): ValidatorFn {
        return (control?: AbstractControl | undefined): ValidationErrors | null => {
            let primaryFound = false;
            for (const participant of control.value) {
                if (participant.role === ParticipantRole.MainArtist) {
                    primaryFound = true;
                }
            }

            if (!primaryFound) {
                return { primaryArtist: true };
            }

            return null;
        };
    }

    maxShareValidator(): ValidatorFn {
        return (control?: AbstractControl | undefined): ValidationErrors | null => {
            if (this.user.role === 'user') {
                return null;
            }

            let sum = 0;
            for (const participant of control.value) {
                sum += participant.share;
            }

            if (sum > 100) {
                return { share: true };
            }

            return null;
        };
    }

    isrcValidator(): ValidatorFn {
        return (control?: AbstractControl | undefined): ValidationErrors | null => {
            if (!control?.value) {
                return null;
            }

            if (!this.utilService.isValidISRC(control.value)) {
                return { invalidEan: true };
            }

            return null;
        }
    }

    previewStartStringToSeconds(mmss: string): number {
        let a = mmss.split(':');
        if (a.length !== 2) {
            return 0;
        }
        return (+a[0]) * 60 + (+a[1]);
    }

    createParticipant(): FormGroup {
        let participant: TrackParticipant = {
            participant: null,
            email: null,
            role: null,
            share: null
        }

        this._changeDetectorRef.markForCheck();
        return this._formBuilder.group(participant);
    }

    addParticipant(participant?: TrackParticipant, event?): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (participant?.participant) {
            participant.email = participant.participant.email;
        }

        const control = <FormArray>this.trackForm.controls['participants'];
        const formGroup = participant ? this._formBuilder.group(participant) : this.createParticipant();
        formGroup['controls'].participant?.valueChanges.subscribe(value => {
            this.filterParticipants(value);
        });
        control.push(formGroup);
        this.trackForm.markAsDirty();
        this._changeDetectorRef.markForCheck();
    }

    onParticipantFocus(index: number) {
        const control = <FormArray>this.trackForm.controls['participants'];
        this.filterParticipants(control.at(index).value);
    }

    deleteParticipant(index: number) {
        const control = <FormArray>this.trackForm.controls['participants'];
        control.removeAt(index);
        if (!control.length) {
            this.addParticipant();
        }
        this.trackForm.markAsDirty();
        this._changeDetectorRef.markForCheck();
    }

    onSubmit(): void {
        if (!this.trackForm.valid) {
            Object.keys(this.trackForm.controls).forEach(field => {
                const control = this.trackForm.get(field);
                control.markAsTouched({ onlySelf: true });
            });
            return;
        }
        let updatedTrack = this.trackForm.getRawValue() as Track;

        for (let [i, participant] of updatedTrack.participants.entries()) {
            updatedTrack.participants[i].share = participant.share / 100;
        }

        for (const participant of updatedTrack.participants) {
            if (typeof participant.participant === "object") {
                participant.email = participant.participant?.email;
            } else {
                participant.participant = {
                    email: participant.email,
                    title: participant.participant,
                }
            }
            delete participant.email;
        }
        this.track = { ...this.track, ...updatedTrack };

        if (this.track.language === '0') {
            this.track.language = null;
        }

        //@ts-ignore
        this.track.previewStart = this.previewStartStringToSeconds(updatedTrack.previewStartString);
        this.matDialogRef.close(this.track);
    }

    createArtist(title: string) {
        return this._matDialog.open(NewArtistModalComponent, {
            autoFocus: true,
            data: {
                title: title,
            }
        }).afterClosed();
        //return this._releasesService.createArtist(artist);
    }

    onArtistBlur(event, index: number): void {
        if (!event.target.value) {
            return;
        }

        const enteredParticipant = event.target.value;
        const controls = <FormArray>this.trackForm.controls['participants'];
        let control = controls.at(index);
        if (!control || !control.dirty) {
            return;
        }
        let controlValue = control.value;

        const artist = this.artists.find(a => a.title?.toLowerCase() === enteredParticipant.toLowerCase());
        if (!artist) {
            this.createArtist(enteredParticipant).subscribe((response: Artist) => {
                if (response) {
                    controlValue.participant = response;
                    if (controlValue.artist_uuid !== undefined) {
                        controlValue.artist_uuid = response.uuid;
                    }
                    control.setValue(controlValue);
                } else {
                    event.target.value = control.value.participant?.title ?? "";
                }
            });
            return;
        }

        controlValue.participant = artist;
        if (controlValue.artist_uuid !== undefined) {
            controlValue.artist_uuid = artist.uuid;
        }
        control.setValue(controlValue);
    }

    participantInputKeyDown(event): void {
        if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    autocompleteMouseDown(event) {
        //This function is needed to prevent the blur event from firing when the user clicks on the autocomplete options
        event.preventDefault();
        event.stopPropagation();
    }

    filterParticipants(artist?: string | TrackParticipant): void {
        let value: string = "";
        if (typeof artist === "string") {
            value = artist;
        } else if (artist != null) {
            value = artist?.participant?.title ?? "";
        }
        this.filteredArtists = this.artists?.filter(artist =>
            (artist ? artist.title.toLowerCase().includes(value.toLowerCase()) : true)
        ).sort((a, b) => a.title > b.title ? 1 : -1) ?? [];

        if (this.filteredArtists?.length > 25) {
            this.filteredArtists = this.filteredArtists.slice(0, 25);
        }
        this._changeDetectorRef.markForCheck();
    }

    getArtistName(inputArtist: Artist) {
        return this.artists?.find(artist => artist.uuid === inputArtist?.uuid)?.title || '';
    }

    onParticipantAutocomplete(event: MatAutocompleteSelectedEvent, index: number) {
        this.trackForm.get(`participants.${index}.email`).setValue(event.option.value.email);
    }

    fillFormData() {
        let isParticipantsEmpty = true;
        for (const participant of this.trackForm.get('participants').value) {
            if ((typeof participant === "string" && participant.length > 0)
                || (typeof participant === "object" && participant.participant?.title?.length > 0)) {
                isParticipantsEmpty = false;
                break;
            }
        }
        if (isParticipantsEmpty) {
            this.trackForm.controls['participants'] = new FormArray([]);
        }

        const fillData = {} as Track;
        for (const fillDataProperty in this.fillData) {
            if (!this.fillData.hasOwnProperty(fillDataProperty)) {
                return;
            }
            if (this.trackForm.get(fillDataProperty)?.value &&
                this.originalTrackData[fillDataProperty] &&
                this.trackForm.get(fillDataProperty).value.length > 0 &&
                this.trackForm.get(fillDataProperty).value !== this.originalTrackData[fillDataProperty]) {
                //Skip
            } else {
                fillData[fillDataProperty] = this.fillData[fillDataProperty];
                if (fillDataProperty === 'genres') {
                    this.track[fillDataProperty] = this.fillData[fillDataProperty];
                } else if (fillDataProperty === 'participants') {
                    for (const participant of this.fillData[fillDataProperty]) {
                        this.addParticipant(participant);
                    }
                }
            }
        }

        this.trackForm.patchValue(fillData);
        this.trackForm.markAsDirty();
        this.trackForm.updateValueAndValidity();
        this._changeDetectorRef.markForCheck();
    }

    playPreview() {
        if (this.audioPlayer === undefined) {
            this.audioPlayer = new Audio();
        } else {
            if (!this.audioPlayer.paused) {
                this.audioPlayer.pause();
                return;
            }
        }

        if (!this.trackForm.get('previewStartString').valid) {
            return;
        }

        this.audioPlayer.removeEventListener('timeupdate', this.previewPlayerTimeUpdate.bind(this));
        //this.audioPlayer.src = this.platformService.getAudioPreviewSrc(this.track);
        fetch(this.platformService.getAudioPreviewSrc(this.track), {
            headers: {
                'Authorization': 'Bearer ' + this._authService.accessToken,
            }
        })
            .then(r => r.blob()) // request as Blob
            .then(blob => {
                if (this.audioPlayer === undefined) {
                    this.audioPlayer = new Audio();
                }
                this.audioPlayer.src = URL.createObjectURL(blob);
                const startTime = this.previewStartStringToSeconds(this.trackForm.get('previewStartString').value);
                this.audioPlayer.currentTime = startTime;
                this._audioStopThreshold = startTime + parseInt(this.trackForm.get('previewDuration').value);
                this.audioPlayer.addEventListener('timeupdate', this.previewPlayerTimeUpdate.bind(this));
                this.audioPlayer.play();
                this._changeDetectorRef.markForCheck();
            })
    }

    previewPlayerTimeUpdate() {
        if (!this.audioPlayer) {
            return;
        }

        if (this.audioPlayer.currentTime >= this._audioStopThreshold || this.audioPlayer.paused) {
            this.audioPlayer.pause();
            this.audioPlayer.removeEventListener('timeupdate', this.previewPlayerTimeUpdate.bind(this));
            this.audioPlayer.remove();
            this.audioPlayer = undefined;
            this._changeDetectorRef.detectChanges();
        }
    }

    compareByUuid = function (option, value): boolean {
        return option.uuid === value?.uuid;
    }

    get trackByUuid() {
        return trackByUuid;
    }
}
